import { Link } from 'react-router-dom';
import type { NewsArticle } from '@/types/content';
import { formatDate } from '@/utils/content';

interface LatestNewsProps {
  news: NewsArticle[];
}

export default function LatestNews({ news }: LatestNewsProps) {
  if (news.length === 0) return null;
  const [featured, ...rest] = news;
  const list = rest.slice(0, 5);

  return (
    <section className="w-full bg-background-50 py-12 md:py-16">
      <div className="mx-auto w-full max-w-6xl px-4 md:px-8">
        <div className="mb-6 flex items-end justify-between">
          <div>
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">最新影视资讯</h2>
            <p className="mt-1 text-sm text-foreground-500">行业观察、新片速递、深度影评，紧跟影视动态</p>
          </div>
          <Link
            to="/news"
            className="flex cursor-pointer items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-600 transition-colors hover:text-primary-700"
          >
            全部资讯
            <i className="ri-arrow-right-s-line" />
          </Link>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <Link
            to={`/news/${featured.id}`}
            className="group flex cursor-pointer flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-all duration-300 hover:border-primary-200"
          >
            <div className="relative h-56 w-full overflow-hidden bg-background-200 md:h-72">
              <img
                src={featured.cover}
                alt={featured.title}
                title={`${featured.title} - 红杏影视资讯`}
                className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
              />
              <span className="absolute left-4 top-4 rounded-md bg-accent-500 px-2.5 py-1 text-xs font-medium text-foreground-950">
                {featured.category}
              </span>
            </div>
            <div className="p-5">
              <h3 className="font-heading text-lg font-bold leading-snug text-foreground-950 group-hover:text-primary-600">
                {featured.title}
              </h3>
              <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">{featured.excerpt}</p>
              <p className="mt-4 flex items-center gap-3 text-xs text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line" />
                  {formatDate(featured.date)}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line" />
                  {featured.views.toLocaleString()}
                </span>
              </p>
            </div>
          </Link>

          <div className="flex flex-col rounded-lg border border-background-200 bg-background-100 p-2">
            {list.map((article) => (
              <Link
                key={article.id}
                to={`/news/${article.id}`}
                className="group flex cursor-pointer items-start gap-4 rounded-md p-3 transition-colors hover:bg-background-50 md:p-4"
              >
                <div className="flex h-10 w-10 shrink-0 flex-col items-center justify-center rounded-md bg-background-50 text-primary-600">
                  <span className="font-heading text-sm font-bold leading-none">
                    {article.date.slice(8, 10)}
                  </span>
                  <span className="mt-0.5 text-[10px] text-foreground-500">{article.date.slice(0, 4)}</span>
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="line-clamp-2 text-sm font-medium leading-snug text-foreground-900 group-hover:text-primary-600">
                    {article.title}
                  </h3>
                  <p className="mt-1.5 flex items-center gap-3 text-xs text-foreground-500">
                    <span>{article.category}</span>
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line" />
                      {article.views.toLocaleString()}
                    </span>
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}